/**
 * One row of the tree (`060-notebook-tree.md` §5, "Tree rows").
 *
 *   [chevron] [icon] Title                                    [⋯] [+]
 *
 * A single line, 28px at default density — density is a stated design value, so the row is
 * bound to `--row-h` and restyles with the density switch rather than hardcoding a height
 * (`110-design-system.md` §4).
 *
 * ## Two things here exist purely for speed, and both are load-bearing at 500 rows
 *
 * **1. Every handler prop takes the page id and is REFERENTIALLY STABLE.** The obvious
 * shape — `onOpen={() => navigate(page.id)}` — allocates a new closure for every row on
 * every render of the tree, which makes `memo` below completely inert: each of the 500 rows
 * re-renders on every keystroke of type-ahead, every arrow key, every drag hover. The
 * parent therefore passes ONE `useCallback` per action, shared by all rows, and the row
 * hands its own id back.
 *
 * **2. The action buttons are only mounted when they can actually be used.** A Radix
 * `DropdownMenu` per row means 500 menu roots, 500 sets of context and subscriptions, all
 * to render a `⋯` that is invisible until you hover it. Mounting them on hover/focus takes
 * a collapse of a big subtree from ~290ms (a visible hitch) to a frame.
 *
 * Neither is a micro-optimisation: `000-overview.md` makes speed of capture a design value,
 * and a sidebar that stutters when you press `↓` is a broken sidebar.
 */
import { memo, useEffect, useRef, useState } from 'react';
import { ChevronRight, FileText, MoreHorizontal, Plus, Star } from 'lucide-react';
import type { TreePage } from '@/api/endpoints/pages';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import type { DropZone } from '@/features/notebook/treeModel';

export interface TreeRowProps {
  page: TreePage;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
  active: boolean;
  /** This row takes DOM focus. Exactly one row at most, and only when the TREE owns focus. */
  focused: boolean;
  /** This row is the tree's single tab stop (roving tabindex). Not the same as `focused`:
   *  when focus has moved OUT of the tree — into the editor, say — the tree must still have
   *  a way back IN via Tab, but it must NOT drag the caret out of the document to do it. */
  tabbable: boolean;
  editing: boolean;
  /** True while this page is the one being dragged. */
  dragging: boolean;
  invalidTarget: boolean;
  dropZone: DropZone | null;
  /** A create that has not yet reached the server: the row exists, durably, on this device.
   *  A subtle dot, not an error (`060` §6). */
  pending: boolean;

  onOpen: (id: string) => void;
  onToggle: (id: string) => void;
  onFocus: (id: string) => void;
  /** Blur: commit the title and stay put. */
  onRename: (id: string, title: string) => void;
  /** `Enter`: commit, and — on a brand-new page — open it with the caret in the body. */
  onRenameAndOpen: (id: string, title: string) => void;
  onStartRename: (id: string) => void;
  onCancelRename: (id: string) => void;
  /** `Tab` commits and creates ANOTHER sibling — for rattling out a list of NPCs (§6). */
  onRenameAndCreateSibling: (id: string, title: string) => void;
  onAddChild: (id: string) => void;
  onArchive: (page: TreePage) => void;
  onToggleFavorite: (page: TreePage) => void;

  onDragStart: (event: React.DragEvent, id: string) => void;
  onDragOver: (event: React.DragEvent, id: string, hasChildren: boolean) => void;
  onDrop: (event: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
}

function TreeRowImpl({
  page,
  depth,
  hasChildren,
  expanded,
  active,
  focused,
  tabbable,
  editing,
  dragging,
  invalidTarget,
  dropZone,
  pending,
  onOpen,
  onToggle,
  onFocus,
  onRename,
  onRenameAndOpen,
  onStartRename,
  onCancelRename,
  onRenameAndCreateSibling,
  onAddChild,
  onArchive,
  onToggleFavorite,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}: TreeRowProps) {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);

  // Local, so hovering a row re-renders THAT row and no other (see the header note).
  const [hovered, setHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Roving tabindex (WAI-ARIA `tree`): the DOM focus has to follow the state, or `↓` moves
  // a highlight the screen reader cannot see.
  useEffect(() => {
    if (focused && !editing) rowRef.current?.focus({ preventScroll: false });
  }, [focused, editing]);

  const showActions = hovered || menuOpen || focused;

  return (
    <div
      ref={rowRef}
      role="treeitem"
      aria-level={depth + 1}
      aria-selected={active}
      aria-expanded={hasChildren ? expanded : undefined}
      data-testid={`tree-row-${page.id}`}
      data-page-id={page.id}
      tabIndex={tabbable ? 0 : -1}
      draggable={!editing}
      onDragStart={(event) => {
        onDragStart(event, page.id);
      }}
      onDragOver={(event) => {
        onDragOver(event, page.id, hasChildren);
      }}
      onDrop={(event) => {
        onDrop(event, page.id);
      }}
      onDragEnd={onDragEnd}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      onFocus={() => {
        onFocus(page.id);
      }}
      onClick={() => {
        if (!editing) onOpen(page.id);
      }}
      onDoubleClick={(event) => {
        event.stopPropagation();
        onStartRename(page.id);
      }}
      className={cn(
        'group relative flex h-[var(--row-h)] cursor-default items-center gap-1 rounded-sm pr-1 select-none',
        'hover:bg-[var(--accent)]',
        active && 'bg-[var(--accent)] font-medium',
        dragging && 'opacity-40',
        invalidTarget && 'cursor-not-allowed',
        // Middle 50% of a valid target: the row itself highlights.
        dropZone === 'inside' && 'bg-[var(--primary)]/15 ring-1 ring-[var(--primary)]/40',
      )}
      style={{ paddingLeft: `${String(depth * 12 + 4)}px` }}
    >
      {/* 2px sibling indicators, indented to the row's depth. */}
      {dropZone === 'before' && (
        <span
          aria-hidden
          className="pointer-events-none absolute top-0 right-1 left-0 h-0.5 bg-[var(--primary)]"
          style={{ marginLeft: `${String(depth * 12 + 4)}px` }}
        />
      )}
      {dropZone === 'after' && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-1 bottom-0 left-0 h-0.5 bg-[var(--primary)]"
          style={{ marginLeft: `${String(depth * 12 + 4)}px` }}
        />
      )}

      {/* The chevron toggles; it does NOT open the page. */}
      {hasChildren ? (
        <button
          type="button"
          aria-label={expanded ? t('notebook.collapse') : t('notebook.expand')}
          tabIndex={-1}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(page.id);
          }}
          className="flex size-4 shrink-0 items-center justify-center rounded-sm text-[var(--muted-foreground)] hover:bg-[var(--border)]"
        >
          <ChevronRight
            className={cn('size-3 transition-transform', expanded && 'rotate-90')}
            strokeWidth={2}
          />
        </button>
      ) : (
        <span className="size-4 shrink-0" />
      )}

      <span className="flex size-4 shrink-0 items-center justify-center text-[var(--muted-foreground)]">
        {page.icon ?? <FileText className="size-3.5" strokeWidth={1.75} />}
      </span>

      {editing ? (
        <input
          autoFocus
          defaultValue={page.title === 'Untitled' ? '' : page.title}
          placeholder={t('notebook.untitled')}
          aria-label={t('notebook.pageTitleLabel')}
          data-testid="tree-rename-input"
          onClick={(event) => {
            event.stopPropagation();
          }}
          onBlur={(event) => {
            onRename(page.id, event.currentTarget.value);
          }}
          onKeyDown={(event) => {
            event.stopPropagation(); // the tree's keymap must not see these keystrokes

            if (event.key === 'Enter') {
              event.preventDefault();
              onRenameAndOpen(page.id, event.currentTarget.value);
            } else if (event.key === 'Tab') {
              event.preventDefault();
              onRenameAndCreateSibling(page.id, event.currentTarget.value);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              // On a CREATE this COMMITS (the row already exists on the server, and silently
              // destroying a thing the user made is the opposite of "never lose data"). On a
              // RENAME it reverts. The parent owns that distinction.
              onCancelRename(page.id);
            }
          }}
          className="h-[calc(var(--row-h)-6px)] min-w-0 flex-1 rounded-sm border border-[var(--ring)] bg-[var(--background)] px-1 text-[length:var(--ui-font)] outline-none"
        />
      ) : (
        <span className="min-w-0 flex-1 truncate" title={page.title}>
          {page.title}
        </span>
      )}

      {pending && (
        <span
          aria-label={t('notebook.notSyncedLabel')}
          title={t('notebook.notSyncedTitle')}
          className="size-1.5 shrink-0 rounded-full bg-[var(--warning)]"
        />
      )}

      {/* Mounted only when reachable. 500 idle Radix menu roots is what a laggy sidebar is
          actually made of. A favorited row is the exception: its star stays lit even when the
          row is neither hovered nor focused, so favorites are glanceable straight from the tree. */}
      {!editing && (showActions || page.isFavorite) && (
        <span className="flex shrink-0 items-center">
          {showActions && (
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('notebook.actionsFor', { title: page.title })}
                  tabIndex={-1}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  className="flex size-5 items-center justify-center rounded-sm text-[var(--muted-foreground)] hover:bg-[var(--border)]"
                >
                  <MoreHorizontal className="size-3.5" strokeWidth={1.75} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <DropdownMenuItem
                  onSelect={() => {
                    onStartRename(page.id);
                  }}
                >
                  {t('common.rename')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    onAddChild(page.id);
                  }}
                >
                  {t('notebook.addSubpage')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => {
                    onArchive(page);
                  }}
                >
                  {t('notebook.trash.title')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button
            type="button"
            aria-label={
              page.isFavorite
                ? t('notebook.removeFromFavorites', { title: page.title })
                : t('notebook.addToFavorites', { title: page.title })
            }
            aria-pressed={page.isFavorite}
            tabIndex={-1}
            onClick={(event) => {
              event.stopPropagation();
              onToggleFavorite(page);
            }}
            className={cn(
              'flex size-5 items-center justify-center rounded-sm hover:bg-[var(--border)]',
              page.isFavorite ? 'text-[var(--warning)]' : 'text-[var(--muted-foreground)]',
            )}
          >
            <Star
              className="size-3.5"
              strokeWidth={1.75}
              fill={page.isFavorite ? 'currentColor' : 'none'}
            />
          </button>

          {showActions && (
            <button
              type="button"
              aria-label={t('notebook.addSubpageFor', { title: page.title })}
              tabIndex={-1}
              onClick={(event) => {
                event.stopPropagation();
                onAddChild(page.id);
              }}
              className="flex size-5 items-center justify-center rounded-sm text-[var(--muted-foreground)] hover:bg-[var(--border)]"
            >
              <Plus className="size-3.5" strokeWidth={1.75} />
            </button>
          )}
        </span>
      )}
    </div>
  );
}

// Now that every handler prop is stable, this actually does something: a keystroke in the
// type-ahead buffer, or a drag hovering row 40, re-renders the rows that CHANGED, not the
// other 499.
export const TreeRow = memo(TreeRowImpl);
